import { api } from './api';

// Types for tenant settings
export interface SearchSettings {
  keyword_matching_enabled: boolean;
}

export interface FeatureSettings {
  ai_summary: boolean;
  auto_tagging: boolean;
  design_verification: boolean;
}

export interface TenantSettings {
  theme: string;
  language: string;
  features: FeatureSettings;
  search: SearchSettings;
}

export interface TenantSettingsResponse {
  settings: TenantSettings;
  tenant_id: string;
  tenant_name: string;
  /** Feature keys this tenant may change; the server refuses anything else. */
  manageable_features?: string[];
}

export interface UpdateTenantSettingsRequest {
  search?: Partial<SearchSettings>;
  features?: Partial<FeatureSettings>;
  theme?: string;
  language?: string;
}

/** Effective limits for the current tenant and user. Mirrors GET /settings/capabilities. */
export interface UploadCapability {
  allowedMimeTypes: string[];
  maxFileSizeMb: number;
  maxFileSizeBytes: number;
  maxSimultaneousFiles: number;
}

export interface LargeUploadCapability {
  enabled: boolean;
  reason?: string | null;
  maxSizeMb: number;
  maxUploadsPerDay: number;
  maxActiveJobs: number;
}

export interface CadCapability {
  enabled: boolean;
  reason?: string | null;
  thicknessEnabled: boolean;
  maxFileSizeMb: number;
  allowedExtensions: string[];
  maxBatchFiles: number;
}

export interface DrbfmCapability {
  claimMaxLength: number;
  evidenceMaxLength: number;
  maxCausalClaims: number;
  maxUnknownAssumptions: number;
  instructionMaxLength: number;
}

export interface Capabilities {
  upload: UploadCapability;
  largeUpload: LargeUploadCapability;
  cad: CadCapability;
  drbfm: DrbfmCapability;
}

class SettingsService {
  /**
   * Get current tenant settings
   * Requires USER role
   */
  async getTenantSettings(): Promise<TenantSettingsResponse> {
    return api.get<TenantSettingsResponse>('/settings/tenant');
  }

  /** Requires USER role; the tenant comes from the token. */
  async getCapabilities(): Promise<Capabilities> {
    return api.get<Capabilities>('/settings/capabilities');
  }

  /**
   * Update tenant settings
   * Requires ADMIN role
   */
  async updateTenantSettings(
    settings: UpdateTenantSettingsRequest
  ): Promise<TenantSettingsResponse> {
    return api.put<TenantSettingsResponse>('/settings/tenant', settings);
  }

  /**
   * Update search settings specifically
   * Convenience method for updating only search-related settings
   */
  async updateSearchSettings(
    searchSettings: Partial<SearchSettings>
  ): Promise<TenantSettingsResponse> {
    return this.updateTenantSettings({ search: searchSettings });
  }

  /**
   * Update feature flags specifically.
   * Only the keys passed are changed — the backend merges field by field, so a
   * sibling flag the caller omits keeps its stored value.
   */
  async updateFeatureSettings(
    features: Partial<FeatureSettings>
  ): Promise<TenantSettingsResponse> {
    return this.updateTenantSettings({ features });
  }
}

export const settingsService = new SettingsService();
